import React, { useState, useRef } from 'react';
import { FileText, ArrowLeft, X, Send, Sparkles, ImageIcon } from 'lucide-react';

interface HomeworkPortalProps {
  subjectName: string | null;
  onSubmit: (message: string) => void;
  onBack: () => void;
}

export const HomeworkPortal: React.FC<HomeworkPortalProps> = ({
  subjectName,
  onSubmit,
  onBack,
}) => {
  const [question, setQuestion] = useState('');
  const [attempted, setAttempted] = useState('');
  const [, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const canSubmit = question.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const parts: string[] = [];
    parts.push(`Homework Question:\n${question.trim()}`);
    if (attempted.trim()) {
      parts.push(`What I've tried:\n${attempted.trim()}`);
    }
    if (subjectName) {
      parts.push(`Subject: ${subjectName}`);
    }
    onSubmit(parts.join('\n\n'));
  };

  return (
    <div className="max-w-2xl mx-auto p-6 sm:p-8">
      {/* Header */}
      <div className="flex items-start gap-4 mb-8">
        <button
          onClick={onBack}
          className="mt-1 p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/20 flex items-center justify-center">
            <FileText className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[var(--text-primary)]">Homework Help</h2>
            <p className="text-sm text-[var(--text-secondary)]">
              Paste your question and get step-by-step guidance
            </p>
          </div>
        </div>
      </div>

      {/* Subject Badge */}
      {subjectName && (
        <div className="mb-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-xs font-medium text-emerald-300">
            <Sparkles className="h-3 w-3" />
            {subjectName}
          </span>
        </div>
      )}

      {/* Form */}
      <div className="space-y-5">
        {/* Question */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            Your Question <span className="text-red-400">*</span>
          </label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Paste or type your homework question here..."
            rows={5}
            className="w-full px-4 py-3 rounded-xl bg-[var(--surface-elevated)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent-primary)]/50 focus:ring-2 focus:ring-[var(--accent-primary)]/10 resize-none text-sm leading-relaxed"
          />
        </div>

        {/* What you've tried */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            What have you tried? <span className="text-[var(--text-tertiary)]">(optional)</span>
          </label>
          <textarea
            value={attempted}
            onChange={(e) => setAttempted(e.target.value)}
            placeholder="Show your working or explain what you've attempted so far..."
            rows={3}
            className="w-full px-4 py-3 rounded-xl bg-[var(--surface-elevated)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent-primary)]/50 focus:ring-2 focus:ring-[var(--accent-primary)]/10 resize-none text-sm leading-relaxed"
          />
        </div>

        {/* Image upload */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            Attach a photo <span className="text-[var(--text-tertiary)]">(optional)</span>
          </label>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
          />
          {imagePreview ? (
            <div className="relative inline-block">
              <img
                src={imagePreview}
                alt="Homework"
                className="h-32 rounded-xl border border-[var(--border-color)] object-cover"
              />
              <button
                onClick={clearImage}
                className="absolute -top-2 -right-2 p-1 rounded-full bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 px-4 py-3 rounded-xl border border-dashed border-[var(--border-color)] bg-[var(--surface-elevated)] hover:bg-[var(--bg-tertiary)] transition-colors text-sm text-[var(--text-secondary)]"
            >
              <ImageIcon className="h-4 w-4" />
              Upload a photo of your homework
            </button>
          )}
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-medium text-sm transition-all duration-200 ${
            canSubmit
              ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
              : 'bg-[var(--bg-tertiary)] text-[var(--text-tertiary)] cursor-not-allowed'
          }`}
        >
          <Send className="h-4 w-4" />
          Get Help
        </button>
      </div>

      {/* Tips */}
      <div className="mt-8 p-4 rounded-xl bg-[var(--surface-elevated)] border border-[var(--border-color)]">
        <h3 className="text-sm font-medium text-[var(--text-primary)] mb-2">Tips for better help</h3>
        <ul className="space-y-1.5 text-xs text-[var(--text-secondary)]">
          <li className="flex items-start gap-2">
            <span className="mt-0.5 text-emerald-400">1.</span>
            Include the full question -- don't leave out parts
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 text-emerald-400">2.</span>
            Show your working so we can see where you got stuck
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 text-emerald-400">3.</span>
            Upload a photo if the question has diagrams or tables
          </li>
        </ul>
      </div>
    </div>
  );
};
